<script setup lang="ts">
import { ref, watch } from 'vue';
import { RouterLink, useRoute } from 'vue-router';

import { activeSectionKey, NAV_SECTIONS, sectionBlocks } from '@/shared/navigation';

/**
 * The app's table of contents: «Главная» and then every section as a group —
 * its coloured icon, its name, and its screens underneath. The same component
 * fills the desktop sidebar and the mobile drawer, so the two can never list
 * different things.
 *
 * Only the current section is expanded (every one of them on the home page):
 * 30 screens at once is a wall, one section's 3–9 is a menu.
 *
 * A section that splits its screens into topics shows each topic as a
 * sub-heading over its own links: sentence case, with a dot of the section's
 * hue sitting on the rail that runs down the open section, so a topic reads as
 * a stop on that line rather than as one more link. The caption is text, not a
 * control: a second accordion inside the first would hide the screens of a
 * section the reader has just opened, and cost one more tap for every one of
 * them.
 *
 * An exercise carries a dumbbell at the right edge — the one thing a row says
 * besides its name, so «where can I practise» is answered by a glance down the
 * column.
 */
const emit = defineEmits<{
  navigate: [];
}>();

const route = useRoute();

const openKeys = ref(new Set<string>());

const syncOpen = () => {
  const active = activeSectionKey(route.path);
  openKeys.value = new Set(active ? [active] : NAV_SECTIONS.map((section) => section.key));
};

watch(() => route.path, syncOpen, { immediate: true });

const toggle = (key: string) => {
  const next = new Set(openKeys.value);
  if (next.has(key)) {
    next.delete(key);
  } else {
    next.add(key);
  }
  openKeys.value = next;
};
</script>

<template>
  <nav
    aria-label="Разделы"
    class="flex flex-col gap-1"
  >
    <RouterLink
      v-slot="{ href, navigate, isExactActive }"
      to="/"
      custom
    >
      <a
        :href="href"
        class="flex min-h-11 items-center gap-3 rounded-md px-2.5 text-sm font-medium transition-colors"
        :class="isExactActive ? 'bg-primary/10 text-primary' : 'text-toned hover:bg-accented/60 hover:text-highlighted'"
        :aria-current="isExactActive ? 'page' : undefined"
        @click="navigate($event); emit('navigate')"
      >
        <span class="flex size-8 shrink-0 items-center justify-center rounded-[10px] bg-stone-100 text-stone-600">
          <UIcon
            name="i-lucide-house"
            class="size-[18px]"
          />
        </span>
        Главная
      </a>
    </RouterLink>

    <div
      v-for="section in NAV_SECTIONS"
      :key="section.key"
      class="flex flex-col"
    >
      <button
        type="button"
        class="flex min-h-11 w-full items-center gap-3 rounded-md px-2.5 text-left text-sm font-semibold text-highlighted transition-colors hover:bg-accented/60"
        :aria-expanded="openKeys.has(section.key)"
        @click="toggle(section.key)"
      >
        <span
          class="flex size-8 shrink-0 items-center justify-center rounded-[10px]"
          :class="section.tone.tile"
        >
          <UIcon
            :name="section.icon"
            class="size-[18px]"
          />
        </span>
        <span class="flex-1 truncate">{{ section.title }}</span>
        <UIcon
          name="i-lucide-chevron-down"
          class="size-4 shrink-0 text-dimmed transition-transform duration-200"
          :class="openKeys.has(section.key) ? 'rotate-0' : '-rotate-90'"
        />
      </button>

      <div
        v-show="openKeys.has(section.key)"
        class="mb-1 ms-[1.625rem] flex flex-col border-s border-default ps-3 pt-0.5"
      >
        <template
          v-for="block in sectionBlocks(section)"
          :key="block.key"
        >
          <p
            v-if="block.title"
            :id="`nav-group-${section.key}-${block.key}`"
            class="relative px-2.5 pb-1 pt-4 text-xs font-semibold text-toned first:pt-2"
          >
            <!-- Centred on the rail: the container's 0.75rem padding plus half its 1px border. -->
            <span
              class="absolute -start-[calc(0.75rem+0.5px)] bottom-[calc(0.25rem+0.5lh)] size-1.5 -translate-x-1/2 translate-y-1/2
                rounded-full bg-current ring-[3px] ring-(--ui-bg-elevated)"
              :class="section.tone.text"
              aria-hidden="true"
            />
            {{ block.title }}
          </p>
          <ul
            class="flex flex-col gap-0.5"
            :aria-labelledby="block.title ? `nav-group-${section.key}-${block.key}` : undefined"
          >
            <li
              v-for="linkItem in block.links"
              :key="linkItem.to"
            >
              <RouterLink
                v-slot="{ href, navigate, isActive }"
                :to="linkItem.to"
                custom
              >
                <a
                  :href="href"
                  class="flex min-h-11 items-center gap-1.5 rounded-md px-2.5 py-1.5 text-sm leading-snug transition-colors lg:min-h-10"
                  :class="isActive
                    ? 'bg-primary/10 font-semibold text-primary'
                    : 'text-muted hover:bg-accented/60 hover:text-highlighted'"
                  :aria-current="isActive ? 'page' : undefined"
                  @click="navigate($event); emit('navigate')"
                >
                  <span class="min-w-0 flex-1">{{ linkItem.title }}</span>
                  <template v-if="linkItem.practice">
                    <UIcon
                      name="i-lucide-dumbbell"
                      class="size-4 shrink-0"
                      :class="isActive ? 'text-primary/70' : 'text-dimmed'"
                      aria-hidden="true"
                    />
                    <span class="sr-only">, тренажёр</span>
                  </template>
                </a>
              </RouterLink>
            </li>
          </ul>
        </template>
      </div>
    </div>
  </nav>
</template>
